import { createCachedFetch } from '@/shared/lib/cache';
import { fetchServiceRegionById, type ServiceRegion } from './service-region.api';

const getServiceRegionById = createCachedFetch(fetchServiceRegionById, 'service-region');

/**
 * 여러 서비스 구역을 병렬로 조회
 * 내부적으로 getServiceRegionById 사용 → 캐시 공유
 */
export async function getServiceRegionsByIds(ids: string[]): Promise<ServiceRegion[]> {
  if (ids.length === 0) return [];

  const uniqueIds = Array.from(new Set(ids.filter((id) => id)));

  try {
    const results = await Promise.allSettled(uniqueIds.map((id) => getServiceRegionById(id)));

    return results
      .filter(
        (result): result is PromiseFulfilledResult<ServiceRegion> => result.status === 'fulfilled'
      )
      .map((result) => result.value);
  } catch (error) {
    console.error('서비스 구역 병렬 조회 실패:', error);
    return [];
  }
}

/**
 * Top-level 구역 찾기
 * superRegionId를 따라 최상위 구역까지 조회. getServiceRegionById 사용 → 캐시 공유
 */
export async function getTopLevelRegionId(regionId: string): Promise<string> {
  let currentRegionId = regionId;
  let currentRegion: ServiceRegion | null = null;
  let depth = 0;
  const maxDepth = 10; // 무한 루프 방지

  while (depth < maxDepth) {
    currentRegion = await getServiceRegionById(currentRegionId);

    if (
      !currentRegion.superRegionId ||
      (currentRegion.depth !== undefined && currentRegion.depth <= 0)
    ) {
      return currentRegionId;
    }

    currentRegionId = currentRegion.superRegionId;
    depth++;
  }

  return currentRegionId;
}

export { getServiceRegionById, type ServiceRegion };
